import React from 'react';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import type { SettingsNavGroup } from './settingsNav';
import { filterNavGroups } from './settingsNav';
import styles from './SettingsSidebar.module.css';

export interface SettingsSidebarProps {
  groups: SettingsNavGroup[];
  selectedId: string;
  hasChanges: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string) => void;
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  groups,
  selectedId,
  hasChanges,
  search,
  onSearchChange,
  onSelect,
}) => {
  const visibleGroups = filterNavGroups(groups, search);

  return (
    <aside className={styles.sidebar} data-test-id="settings-sidebar">
      <h2>Settings</h2>
      <div className={styles.search}>
        <Search size={14} className={styles.searchIcon} aria-hidden="true" />
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search settings"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          data-test-id="settings-sidebar-search"
        />
      </div>
      {visibleGroups.length === 0 ? (
        <p className={styles.empty}>No matching settings</p>
      ) : (
        visibleGroups.map((group) => (
          <div key={group.id} className={styles.group} data-test-id={`settings-group-${group.id}`}>
            <h3 className={styles.groupTitle}>{group.label}</h3>
            <ul className={styles.list}>
              {group.items.map((item) => (
                <li
                  key={item.id}
                  className={clsx(styles.item, selectedId === item.id && styles.itemActive)}
                >
                  <button
                    type="button"
                    className={styles.itemButton}
                    onClick={() => onSelect(item.id)}
                    data-test-id={`settings-sidebar-item-${item.id}`}
                  >
                    <span className={styles.itemName}>{item.label}</span>
                    {item.id === selectedId && hasChanges && (
                      <span className={styles.unsavedIndicator} title="Unsaved changes">
                        *
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </aside>
  );
};

export default SettingsSidebar;
